import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { FormBuilder } from '@tf/form-builder';
import { Box, Button, Checkbox, Icon, Select, TFNotifier } from '@tf/ui';
import { S, type SubmitHandler, T } from '@tf/utils';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetEntityLabel } from '@/components/shared';
import { useCreateApplicationMutation } from '@/core/api';
import { useAppEnv, useDefinitions } from '@/core/providers';
import { endClientAreaPaths } from '@/routes/endClientArea/paths';

interface AddApplicationFormProps {
	isInModal?: boolean;
	onClose?: () => void;
}

export const AddApplicationForm: React.FC<AddApplicationFormProps> = ({ isInModal = false, onClose }) => {
	const { _ } = useLingui();
	const navigate = useNavigate();

	const appEnv = useAppEnv();
	const { defs, scripts, currency } = useDefinitions();
	const getEntityLabel = useGetEntityLabel();

	const applicationKindOptions = Object.entries(defs.accounts)
		.map(([applicationKind]) => {
			const label = getEntityLabel(applicationKind);
			return { value: applicationKind, label: label || S.prettify(applicationKind) };
		})
		.sort((a, b) => a.value.localeCompare(b.value));

	T.assertNotNullish(applicationKindOptions[0].value, 'at least one application kind should be available');
	const [selectedApplicationKind, setSelectedApplicationKind] = useState<string | null>(
		applicationKindOptions[0].value,
	);

	const getApplicantKinds = (applicationKind: string | null) => {
		if (!applicationKind) {
			return [];
		}
		const applicantConnection = defs.entities[applicationKind]?.possibleConnections.find((c) => c.kind === 'APPLICANT');
		return applicantConnection?.fromEntities.map((e) => e.kind) ?? [];
	};

	const availableApplicantKinds = getApplicantKinds(selectedApplicationKind);
	const [selectedApplicantKind, setSelectedApplicantKind] = useState<string | null>(availableApplicantKinds[0] ?? null);

	const applicantKind =
		selectedApplicantKind && availableApplicantKinds.includes(selectedApplicantKind)
			? selectedApplicantKind
			: (availableApplicantKinds[0] ?? null);

	const handleApplicationKindChange = (value: string | null) => {
		setSelectedApplicationKind(value);
		setSelectedApplicantKind(getApplicantKinds(value)[0] ?? null);
	};

	const entitySelfSegmentKind = applicantKind ? defs.selfSegments[applicantKind] : null;

	const handleError = (error: Error) => {
		Sentry.captureException(error);
		TFNotifier.error(
			_(
				msg({
					id: 'addApplicationModal.notification.textRequired',
					message: 'Failed to create application',
				}),
			),
		);
	};

	const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);

	const createAccountMutation = useCreateApplicationMutation();
	const submitHandler: SubmitHandler = async ({ values }) => {
		if (!selectedApplicationKind || !applicantKind || !entitySelfSegmentKind) {
			handleError(new Error('No account kind selected'));
			return;
		}

		const requiredConnections =
			availableApplicantKinds.length > 1 ? [{ connectionKind: 'APPLICANT', entityKind: applicantKind }] : undefined;

		createAccountMutation.mutate(
			{
				applicationKind: selectedApplicationKind,
				entitySelfSegmentKind,
				entitySelfSegmentData: values[entitySelfSegmentKind],
				requiredConnections,
			},
			{
				onSuccess: (res) => {
					onClose?.();
					navigate(endClientAreaPaths.application(res.connectedEntityId));
				},
				onError: handleError,
			},
		);
	};

	return (
		<>
			<Select
				m={isInModal ? '1rem' : '0'}
				mb="0.25rem"
				label="Account type"
				placeholder="Select an account type"
				data={applicationKindOptions}
				data-autofocus
				value={selectedApplicationKind}
				onChange={handleApplicationKindChange}
			/>

			{availableApplicantKinds.length > 1 && (
				<Select
					m={isInModal ? '0 1rem' : '0'}
					mb="0.25rem"
					label="Applicant type"
					placeholder="Select an applicant type"
					data={availableApplicantKinds.map((kind) => {
						const label = getEntityLabel(kind);
						return { value: kind, label: label || S.prettify(kind) };
					})}
					value={applicantKind}
					onChange={setSelectedApplicantKind}
				/>
			)}

			{entitySelfSegmentKind && (
				<FormBuilder
					key={entitySelfSegmentKind}
					currency={currency}
					segments={[
						{
							segmentKind: entitySelfSegmentKind,
							graphId: 0,
							accessMode: 'MODIFY',
						},
					]}
					shouldValidate
					apiConfig={{
						username: '',
						clusterId: appEnv.clusterId,
					}}
					processingScripts={scripts}
					defs={{
						structs: defs.structs,
						options: defs.options,
					}}
					styles={{ content: { padding: isInModal ? '.5rem 1rem 0.75rem' : '0' } }}
					submitButtonRenderer={() => {
						return (
							<Box p={isInModal ? '.75rem 1rem 1rem' : '1rem 0'}>
								<Checkbox
									checked={isAgreeToTerms}
									onChange={(e) => setIsAgreeToTerms(e.target.checked)}
									label={
										<span>
											<Trans id="addApplicationModal.dataProcessingAgreementCheckbox">
												I agree to the processing of personal data by Trustform, in accordance with their{' '}
												<a
													href="https://cdn.trustform.io/documents/Privacy%20Notice_Trustform_2025.pdf"
													target="_blank"
													rel="noreferrer"
												>
													Privacy Notice
												</a>
											</Trans>
										</span>
									}
								/>
								<Button
									type="submit"
									loading={createAccountMutation.isPending}
									disabled={!isAgreeToTerms}
									fullWidth
									size="md"
									mt={'1.5rem'}
									leftSection={<Icon.IconFilePlus size={16} />}
									sx={{
										'& .mantine-Button-section': {
											marginRight: '8px',
										},
									}}
								>
									{_(
										msg({
											id: 'addApplicationModal.startApplicationButton',
											message: 'Create new application',
										}),
									)}
								</Button>
							</Box>
						);
					}}
					onSubmit={submitHandler}
				/>
			)}
		</>
	);
};
